/* import __COLOCATED_TEMPLATE__ from './feedback-survey.hbs.js'; */
var _dec, _dec2, _dec3, _dec4, _dec5, _dec6, _dec7, _dec8, _class, _class2, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import FeedbackSurveyQuestionMultipleChoiceModal from '@dashboard/shared-ui/components/feedback-survey/question-multiple-choice-modal';
import { classNames, classNameBindings } from '@ember-decorators/component';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { readOnly } from '@ember/object/computed';
/* eslint square/no-restricted-files:"off" -- unscoped components in this folder are discouraged */
import { cancel, later, next } from '@ember/runloop';
import { assert, warn } from '@ember/debug';
import { isNone } from '@ember/utils';
import Component from '@ember/component';
import { QuestionType } from '@square/dashboard-protos/models/squareup/feedbacksurveys/common';
import { getWithExpiry, setWithExpiry } from '@dashboard/shared-ui/utils/local-storage';
import environment from '@square/environment-utils/environment';
import { feature } from '@square/ember-features/macros';
import Survey from '@dashboard/shared-ui/models/survey';
import { surveyRendered, surveyDismissed, surveyCompleted, surveyQuestionAnswered } from '@dashboard/shared-ui/events/feedback-surveys/event-catalog';
import Ember from 'ember';
import QuestionFreeform from './feedback-survey/question-freeform';
import QuestionCSAT from './feedback-survey/question-csat';
import QuestionMultipleChoicePrompt from './feedback-survey/question-multiple-choice-prompt';
import QuestionMultipleChoiceInitialPrompt from './feedback-survey/question-multiple-choice-initial-prompt';

/**
## Get Started with Feedback Surveys
See the [`feedback-surveys` service documentation](/services/feedback-surveys/overview.html) for a general
introduction to how the `feedback-surveys` application works and how you can get started with creating surveys,
as well as the different implementation strategies.

If you're ready to add a survey to Dashboard using the default implementation,
then this is the component you want to use!


## Adding Your Survey to Dashboard
Copy the generated tokens for the survey you've created on staging ([go/feedback-surveys-staging](https://feedback-surveys.stage.sqprod.co/app/)).
This is your `stagingSurveyToken`. Pass it to the `feedback-surveys` component to display your survey on staging.

```hbs
{{!-- Will only show surveys on staging. For testing only. --}}
<FeedbackSurvey stagingSurveyToken="a73db13d-dd70-456e-a758-ed5f91a5d626"/>
```

Once you've verified the survey appears as expected, create the same survey in production ([go/feedback-surveys](https://feedback-surveys.sqprod.co/app/))
and add a `productionSurveyToken` to your component.

__IMPORTANT:__ If you do not provide a `productionSurveyToken` that maps to a survey that was
created at [go/feedback-surveys](https://feedback-surveys.sqprod.co/app/),
your survey will __not__ be displayed to any merchants on production. Be sure to actually test
your survey on production, since you use a different token than on staging!


```hbs
<FeedbackSurvey @stagingSurveyToken="a73db13d-dd70-456e-a758-ed5f91a5d626"
                @productionSurveyToken="628a6a2f-2bd7-4648-8cd1-71f455107a7c"/>
```

Any [supported question type](/services/feedback-surveys/question-types.html) will automatically be rendered
for you, and responses will be saved and available in the survey app for the correct environment.
Events are automatically tracked. See your survey overview page in the survey app for the exact query to run
to find events for your survey.

Remember the [general survey limitations](/services/feedback-surveys/overview.html#general-limitations):

- Question prompt text is displayed exactly as written in the survey app (i.e. no localization support) unless a 'onHandleTranslation' argument is provided.
  + The `feedback-survey` component __only supports `en-US` localization__. If a user has a different language or country code, no surveys will be shown to them to prevent them from accidentally seeing unlocalized content.
  + If you need to show localized surveys, you can either handle the translations yourself by providing a 'onHandleTranslation' argument or use a [custom implementation](/services/feedback-surveys/custom.html).
- Surveys are not targeted. If you only want to show your survey to a subset of sellers, you'll need to selectively
show it yourself based on your own logic.


For example, if you only want to show a survey to sellers using your new feature, you might do:

```hbs
{{#if currentUser.canUseMyNewFeature}}
  <FeedbackSurvey stagingSurveyToken="STAGING-TOKEN"
                  productionSurveyToken="PRODUCTION-TOKEN"
                  @afterFade={{action "customCodeToRunAfterDismissal"}}/>
{{/if}}
```

After the survey is dismissed or answered, a key is stored to local storage so it won’t appear again.
You may pass in a custom action to run after the survey is faded out. Local storage is browser specific, so if you dismiss your survey and want to view it again, you can go to your browser's LocalStorage and delete the key
`FeedbackSurvey.{surveyToken}.{merchantToken}`, or you can use a new browser.


## Testing with feedback-survey
If you are testing a page that includes a `feedback-survey` component, you can use the
`feedback-survey` test support functions to help mock out the server requests the component uses.

```js
import { setupFeedbackSurveyResponses } from '@dashboard/shared-ui/test-support/feedback-surveys/rpc';

before(function() {
  setupFeedbackSurveyResponses(new FakeFetch(sandbox));
});
```

If you want to actually verify that a request was correctly formed by the component, you
use the provided helpers.

```js
import {
  setupFeedbackSurveyResponses,
  getSurveyRequests,
  createResponseRequests
} from '@dashboard/shared-ui/test-support/feedback-surveys/rpc';

before(function() {
  this.fakeFetch = new FakeFetch(sandbox);
  setupFeedbackSurveyResponses(this.fakeFetch);
});

it('sends the right requests', async function() {
  // must use await, as these helpers return promises!
  const surveyRequests = await getSurveyRequests(this.fakeFetch);
  const responseRequests = await createResponseRequests(this.fakeFetch);
});

```

### Parameters
@param {string} [stagingSurveyToken] - Required - staging token for the survey. (generated when you create a survey at [go/feedback-surveys-staging](https://feedback-surveys.stage.sqprod.co/app/))
@param {string} [productionSurveyToken] - Required - production token for the survey. (generated when you create a survey at [go/feedback-surveys](https://feedback-surveys.sqprod.co/app/))
@param {boolean} [shouldAnimateIn] - Optional - will add a 1s fade-in animation
@param {onRetrievalCallback} [onRetrieval] - Callback that is triggered after the component is rendered. It is NOT
guaranteed to be triggered as the component may not be rendered for the merchant if no survey is loaded.
@param {Function} [afterFade] - Callback that runs after dismissal of the feedback survey. Gets the response if available as input.
@param {string} [customPrompt] - Custom initial prompt to be displayed for a multiple choice question.
@param {Function} [onHandleTranslation] - Custom handler for localizing the survey prompts/questions/options.
*/

/**
 * @callback onRetrievalCallback
 * @argument didRender - Whether or not the survey component was rendered.
 */

// Used for testing fade out
let _asyncTaskToDo = null;
export function runNextAsyncTask() {
  // Used only in testing to trigger hiding of component after delay
  _asyncTaskToDo();
}
const FADE_OUT_AFTER_DURATION = 2000;
const TIME_TO_LIVE = 1000 * 60 * 60 * 24; // 1 day
let FeedbackSurvey = (_dec = classNames('feedback-survey'), _dec2 = classNameBindings('isOpen:feedback-survey--open', 'shouldAnimateIn:feedback-survey--animate-in'), _dec3 = readOnly('currentSurvey.currentQuestion'), _dec4 = readOnly('currentSurvey.isCompleted'), _dec5 = feature('dashboard/new_feedback_surveys'), _dec6 = computed('userInfo.languageCode', 'shouldForceDisplayFeedbackSurvey', 'onHandleTranslation'), _dec7 = computed('canSeeFeedbackSurveys', 'currentSurvey.surveyToken', 'userInfo.token'), _dec8 = computed('currentQuestion.questionType', 'newFeedbackSurveysEnabled'), _dec(_class = _dec2(_class = (_class2 = class FeedbackSurvey extends Component {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "eventstream", _descriptor, this);
    _initializerDefineProperty(this, "modal", _descriptor2, this);
    _initializerDefineProperty(this, "userInfo", _descriptor3, this);
    _defineProperty(this, "currentSurvey", null);
    _defineProperty(this, "shouldAnimateIn", false);
    _defineProperty(this, "hideRemindMeLaterButton", false);
    _defineProperty(this, "onRetrieval", () => {});
    _defineProperty(this, "afterFade", () => {});
    _initializerDefineProperty(this, "currentQuestion", _descriptor4, this);
    _initializerDefineProperty(this, "surveyCompleted", _descriptor5, this);
    _initializerDefineProperty(this, "newFeedbackSurveysEnabled", _descriptor6, this);
    // This parameter was added specifically for ACTVN-772
    // This default Feedback Survey component does not support localization
    // and should only be shown to en-US merchants
    // !! Use with caution. This will load surveys using the en-US locale regardless of the users’ actual locale !!
    _defineProperty(this, "shouldForceDisplayFeedbackSurvey", false);
  }
  get canSeeFeedbackSurveys() {
    return this.shouldForceDisplayFeedbackSurvey || this.userInfo.languageCode === 'en-US' || Boolean(this.onHandleTranslation);
  }
  get shouldDisplay() {
    const hasRemindMeLater = getWithExpiry(this.userInfo.token) === this.currentSurvey?.surveyToken;
    return !isNone(this.currentSurvey) && this.canSeeFeedbackSurveys && !hasRemindMeLater;
  }
  didInsertElement(...args) {
    super.didInsertElement(...args);
    let token = null;
    if (environment().isProduction) {
      assert('productionSurveyToken required', this.productionSurveyToken);
      token = this.productionSurveyToken;
    } else {
      assert('stagingSurveyToken required', this.stagingSurveyToken);
      warn('productionSurveyToken required', this.productionSurveyToken, {
        id: 'feedback-survey.productionSurveyToken'
      });
      token = this.stagingSurveyToken;
    }
    // Always pass in en-US locale if shouldForceDisplayFeedbackSurvey is true or onHandleTranslation is set
    const locale = this.shouldForceDisplayFeedbackSurvey || this.onHandleTranslation ? 'en-US' : this.userInfo.languageCode;
    const currentSurvey = Survey.create({
      surveyToken: token,
      locale,
      merchantToken: this.userInfo.token,
      employmentToken: this.userInfo.employmentToken
    });
    currentSurvey.load().then(shouldShow => {
      if (this.isDestroyed || this.isDestroying) {
        return;
      }
      if (shouldShow) {
        this.set('currentSurvey', currentSurvey);
        next('afterRender', () => {
          this.set('isOpen', this.shouldDisplay);
          this.onRetrieval && this.onRetrieval(true);
        });
        this.eventstream.track(surveyRendered(currentSurvey.surveyToken));
      } else {
        this.onRetrieval && this.onRetrieval(false);
      }
    });
  }
  willDestroy(...args) {
    super.willDestroy(...args);
    if (this._fadeOutTimer) {
      cancel(this._fadeOutTimer);
      delete this._fadeOutTimer;
    }
  }
  queueAsyncTask(task) {
    // If testing, task is pushed on to queue
    // task will be executed manually
    if (Ember.testing) {
      _asyncTaskToDo = task;
    } else {
      task();
    }
  }
  fadeOutComponentAfterDelay(delay = FADE_OUT_AFTER_DURATION) {
    this.queueAsyncTask(() => {
      this._fadeOutTimer = later(() => this.set('currentSurvey', null), delay);
    });
  }
  get componentClass() {
    const questionType = this.currentQuestion?.questionType;
    switch (questionType) {
      case QuestionType.FREE_FORM:
        return QuestionFreeform;
      case QuestionType.CSAT:
        return QuestionCSAT;
      case QuestionType.MULTIPLE_SELECT:
      case QuestionType.SINGLE_SELECT:
        if (this.newFeedbackSurveysEnabled) {
          return QuestionMultipleChoicePrompt;
        }
        return QuestionMultipleChoiceInitialPrompt;
      default:
        // This return appeases the linter "consistent-return"
        return assert(`Unknown question type ${questionType}`);
    }
  }
  next(response) {
    this.currentSurvey.setResponseAndIncrementSurvey(response).then(() => {
      const surveyToken = this.currentSurvey?.surveyToken;
      const questionToken = response.questionToken;
      this.eventstream.track(surveyQuestionAnswered(surveyToken, questionToken));
      if (this.surveyCompleted) {
        this.eventstream.track(surveyCompleted(surveyToken));
        this.set('isOpen', false);
        this.fadeOutComponentAfterDelay();
        this.afterFade(response);
      }
    });
  }
  remindMeLater() {
    this.set('isOpen', false);
    setWithExpiry(this.userInfo.token, this.currentSurvey?.surveyToken, TIME_TO_LIVE);
  }
  dismiss() {
    this.currentSurvey.setDismissed();
    this.set('isOpen', false);
    if (!this.surveyCompleted) {
      this.eventstream.track(surveyDismissed(this.currentSurvey?.surveyToken));
    }
    this.fadeOutComponentAfterDelay();
    this.afterFade();
  }
  openSurveyModal() {
    this.set('isOpen', false);
    this.modal.open(FeedbackSurveyQuestionMultipleChoiceModal, {
      survey: this.currentSurvey,
      afterFade: this.afterFade
    });
  }
}, _descriptor = _applyDecoratedDescriptor(_class2.prototype, "eventstream", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class2.prototype, "modal", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class2.prototype, "userInfo", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class2.prototype, "currentQuestion", [_dec3], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor5 = _applyDecoratedDescriptor(_class2.prototype, "surveyCompleted", [_dec4], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor6 = _applyDecoratedDescriptor(_class2.prototype, "newFeedbackSurveysEnabled", [_dec5], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class2.prototype, "canSeeFeedbackSurveys", [_dec6], Object.getOwnPropertyDescriptor(_class2.prototype, "canSeeFeedbackSurveys"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "shouldDisplay", [_dec7], Object.getOwnPropertyDescriptor(_class2.prototype, "shouldDisplay"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "componentClass", [_dec8], Object.getOwnPropertyDescriptor(_class2.prototype, "componentClass"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "next", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "next"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "remindMeLater", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "remindMeLater"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "dismiss", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "dismiss"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "openSurveyModal", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "openSurveyModal"), _class2.prototype), _class2)) || _class) || _class);
export { FeedbackSurvey as default };